import { ArrowUpRight, ChevronDown, X, Search, ChevronsUpDown, ArrowUpDown, Star } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { setShowBackToDashboardButton, setSelectedSender } from '../../store/reducers';
import { useState, useEffect, useMemo } from 'react';
import { useLanguage, getReviewSenderDetailsTranslation } from '../../hooks/useLanguage';
import AiIcon from '../../assets/ai-icon.png';

export const ReviewSenderDetails = ({ contacts, navigate, declarationIndex = 0 }) => {
    const dispatch = useDispatch();
    const selectedSenders = useSelector(state => state.user.selectedSender || []);
    const selectedSender = selectedSenders[declarationIndex] || null;
    const suggestions = useSelector(state => {
      const suggestionsState = state.suggestions;
      const suggestionsArray = suggestionsState?.suggestions;
      
      return Array.isArray(suggestionsArray) ? suggestionsArray : [];
    });
    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('name-asc');
    const [isSortOpen, setIsSortOpen] = useState(false);
    const [isSpanish] = useLanguage();

    // Get suggestions for current declaration index
    // Validate suggestions array before using find
    const currentSuggestions = Array.isArray(suggestions) && suggestions.length > 0
      ? suggestions.find(s => s !== null && s !== undefined && typeof s === 'object' && s.exportacion_index === declarationIndex)
      : null;
    const suggestedSenders = (currentSuggestions?.senders && Array.isArray(currentSuggestions.senders)) 
      ? currentSuggestions.senders 
      : [];
    
    // Match suggested senders to contacts by id, nif, identificacion_fiscal, or name
    const matchedSuggestedSenders = suggestedSenders
        .map(suggestion => {
            const matchedContact = contacts.find(contact => 
                contact.id === suggestion.id ||
                (contact.nif && suggestion.nif && contact.nif === suggestion.nif) ||
                (contact.identificacion_fiscal && suggestion.identificacion_fiscal && contact.identificacion_fiscal === suggestion.identificacion_fiscal) ||
                (contact.nombre && suggestion.nombre && contact.nombre.toLowerCase() === suggestion.nombre.toLowerCase())
            );
            return matchedContact ? { ...matchedContact, score: suggestion.score, reason: suggestion.reason } : null;
        })
        .filter(Boolean)
        .sort((a, b) => (b.score || 0) - (a.score || 0)) // Sort by score descending
        .slice(0, 3); // Limit to top 3

    const sortOptions = [
        { value: 'name-asc', label: getReviewSenderDetailsTranslation('nameAZ', isSpanish) },
        { value: 'name-desc', label: getReviewSenderDetailsTranslation('nameZA', isSpanish) },
        { value: 'date-desc', label: getReviewSenderDetailsTranslation('newestFirst', isSpanish) },
        { value: 'date-asc', label: getReviewSenderDetailsTranslation('oldestFirst', isSpanish) }
    ];

    const handleSenderSelect = (sender) => {
        dispatch(setSelectedSender(sender, declarationIndex));
        setIsModalOpen(false);
        setSearchQuery('');
    };

    // Filter contacts by search query across multiple fields
    const filteredContacts = useMemo(() => {
        if (!searchQuery || typeof searchQuery !== 'string') {
            return contacts;
        }
        
        const query = searchQuery.toLowerCase().trim();
        if (!query) {
            return contacts;
        }
        
        return contacts.filter(contact => {
            if (!contact || typeof contact !== 'object') {
                return false;
            }
            
            // Helper function to safely convert and search a field
            const searchInField = (field) => {
                if (field == null) return false;
                if (typeof field === 'string' && field.trim() === '') return false;
                if (typeof field === 'number') {
                    return String(field).toLowerCase().includes(query);
                }
                const fieldStr = String(field).toLowerCase().trim();
                return fieldStr.length > 0 && fieldStr.includes(query);
            };
            
            // Search across multiple fields
            return searchInField(contact.nombre) ||
                   searchInField(contact.identificacion_fiscal) ||
                   searchInField(contact.nif) ||
                   searchInField(contact.eori) ||
                   searchInField(contact.direccion) ||
                   searchInField(contact.localidad) ||
                   searchInField(contact.pais);
        });
    }, [contacts, searchQuery]);

    // Sort filtered contacts
    const sortedContacts = useMemo(() => {
        return [...filteredContacts].sort((a, b) => {
            switch (sortBy) {
                case 'name-asc':
                    return (a.nombre || '').localeCompare(b.nombre || '');
                case 'name-desc':
                    return (b.nombre || '').localeCompare(a.nombre || '');
                case 'date-asc':
                    return new Date(a.date_created || 0) - new Date(b.date_created || 0);
                case 'date-desc':
                    return new Date(b.date_created || 0) - new Date(a.date_created || 0);
                default:
                    return 0;
            }
        });
    }, [filteredContacts, sortBy]);

    const getSortLabel = () => {
        return sortOptions.find(option => option.value === sortBy)?.label || getReviewSenderDetailsTranslation('sortBy', isSpanish);
    };

    return (
        <div className="text-left text-sm flex flex-col" id="review-sender">
            <div className='flex justify-between gap-4 mb-2 items-center'>
                <h1 className='font-semibold'>{getReviewSenderDetailsTranslation('sender', isSpanish)}</h1>
            </div>
            
            <div className={`p-4 border-container rounded-xl form ${selectedSender ? "flex-1" : ""}`}>
                <div className='relative w-full'>
                    <button className={`w-full flex gap-2 justify-between items-center ${selectedSender ? "border-container rounded-md p-4 " : ""}`} onClick={() => setIsModalOpen(true)}>
                        {selectedSender ? <p className='truncate'>{selectedSender.nombre}</p> : <p className='opacity-75 truncate'>{getReviewSenderDetailsTranslation('selectSender', isSpanish)}</p>}
                        <ChevronsUpDown className="w-4 h-4" />
                    </button>
                </div>
                {selectedSender && (
                    <>
                        <div className='review-flex flex gap-4 mt-4'>
                            <div className="form-group flex-1" id={`sender_nombre${declarationIndex === 0 ? '' : `_${declarationIndex + 1}`}`}>
                                <label htmlFor="companyName">{getReviewSenderDetailsTranslation('senderName', isSpanish)}</label>
                                <p className='font-bold'>{selectedSender && selectedSender.nombre || "N/A"}</p>
                            </div>
                            <div className="form-group flex-1" id={`sender_nif${declarationIndex === 0 ? '' : `_${declarationIndex + 1}`}`}>
                                <label htmlFor="address">{getReviewSenderDetailsTranslation('senderTaxId', isSpanish)}</label>
                                <p className='font-bold'>{selectedSender && selectedSender.nif || "N/A"}</p>
                            </div>
                        </div>
                        <div className='review-flex flex gap-4'>
                            <div className="form-group flex-1" id={`sender_eori${declarationIndex === 0 ? '' : `_${declarationIndex + 1}`}`}>
                                <label htmlFor="taxId">{getReviewSenderDetailsTranslation('senderEori', isSpanish)}</label>
                                <p className='font-bold'>{selectedSender && selectedSender.eori  || "N/A"}</p>
                            </div>
                            <div className="form-group flex-1" id={`sender_direccion${declarationIndex === 0 ? '' : `_${declarationIndex + 1}`}`}>
                                <label htmlFor="eori">{getReviewSenderDetailsTranslation('senderAddress', isSpanish)}</label>
                                <p className='font-bold'>{selectedSender && selectedSender.direccion  || "N/A"}</p>
                            </div>
                        </div>
                        <div className='review-flex flex gap-4'>
                            <div className="form-group flex-1" id={`sender_localidad${declarationIndex === 0 ? '' : `_${declarationIndex + 1}`}`}>
                                <label htmlFor="city">{getReviewSenderDetailsTranslation('senderCity', isSpanish)}</label>
                                <p className='font-bold'>{selectedSender && selectedSender.localidad  || "N/A"}</p>
                            </div>
                            <div className="form-group flex-1" id={`sender_pais${declarationIndex === 0 ? '' : `_${declarationIndex + 1}`}`}>
                                <label htmlFor="postalCode">{getReviewSenderDetailsTranslation('senderCountry', isSpanish)}</label>
                                <p className='font-bold'>{selectedSender && selectedSender.pais  || "N/A"}</p>
                            </div>
                        </div>
                    </>
                )}
            </div>



             {/* Suggested Senders */}
             {!selectedSender && matchedSuggestedSenders.length > 0 && (
                    <>
                    <h2 className="text-xs font-light mt-4">{getReviewSenderDetailsTranslation('suggestedSenders', isSpanish)}</h2>
                <div className="flex flex-wrap gap-2 mt-2">
                    {matchedSuggestedSenders.map((suggestedSender, index) => (
                        <button
                            key={`suggested-sender-${declarationIndex}-${suggestedSender.id || index}-${index}`}
                            onClick={() => handleSenderSelect(suggestedSender)}
                            className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-full transition-colors"
                            title={suggestedSender.reason || `Score: ${(suggestedSender.score * 100).toFixed(0)}%`}
                        >
                          <img src={AiIcon} alt="AI Icon" className="w-4 h-4" />
                            <span className="font-medium text-xs truncate">{suggestedSender.nombre}</span>
                            {suggestedSender.score && (
                                <span className="text-[8px] text-blue-400 font-semibold ">
                                    {(suggestedSender.score * 100).toFixed(0)}%
                                </span>
                              )}
                        </button>
                    ))}
                </div>
                </>
            )}

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">{getReviewSenderDetailsTranslation('selectSender', isSpanish)}</h2>
                            <button onClick={() => {
                                setIsModalOpen(false);
                                setSearchQuery('');
                            }} className="p-1 hover:bg-gray-100 rounded-full">
                                <X className="w-6 h-6" />
                            </button>
                        </div>
       
                        <div className="flex gap-2 mb-4">
                            {/* Search Input */}
                            <div className="relative flex-1">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Search className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    className="w-full pl-10 pr-4 py-2 border-container rounded-full "
                                    placeholder={getReviewSenderDetailsTranslation('searchSenders', isSpanish)}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>

                            {/* Custom Sort Dropdown */}
                            <div className="relative button-no-hover-effect">
                                <button
                                    className="flex items-center gap-2 px-2 pr-0 py-2 rounded-full"
                                    onClick={() => setIsSortOpen(!isSortOpen)}
                                >
                                    <ArrowUpDown className="w-4 h-4" />
                                    <span>{getSortLabel()}</span>
                                    <ChevronDown className={`w-4 h-4 transition-transform ${isSortOpen ? 'rotate-180' : ''}`} />
                                </button>
                                
                                {isSortOpen && (
                                    <div className="overflow-hidden absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border-container py-1 z-10">
                                        {sortOptions.map((option) => (
                                            <button
                                                key={option.value}
                                                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                                                    sortBy === option.value ? 'text-primary' : 'text-gray-700'
                                                }`}
                                                onClick={() => {
                                                    setSortBy(option.value);
                                                    setIsSortOpen(false);
                                                }}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>        

                        <div className="max-h-[60vh] min-h-[60vh] overflow-y-auto">
                            {contacts.length > 0 ? (
                                <div className="space-y-0">
                                    {sortedContacts.length > 0 ? (
                                        sortedContacts.map((contact, index) => (
                                            <div 
                                                key={`sender-${declarationIndex}-${contact.id || index}-${index}`} 
                                                className='p-3 px-0 hover:bg-gray-100 cursor-pointer border-bottom-gray'
                                                onClick={() => handleSenderSelect(contact)}
                                            >
                                                <div className="flex justify-between items-start gap-2">
                                                    <div>
                                                        <p className="font-medium">{contact.nombre}</p>
                                                        <p className="text-sm text-gray-500">
                                                            {contact.pais}
                                                        </p>
                                                    </div>
                                                    {(contact.nif) && (
                                                        <span className="text-xs bg-bgGray px-3 py-1 rounded-full">
                                                            {contact.nif || contact.nif}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center py-4 text-gray-500">
                                            {getReviewSenderDetailsTranslation('noSendersFound', isSpanish)} "{searchQuery}"
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    <p className='opacity-75 mb-4'>{getReviewSenderDetailsTranslation('noSendersAvailable', isSpanish)}</p>
                                    <button 
                                        className='flex gap-2 items-center justify-center mx-auto px-4 py-2 bg-default text-white rounded-full'
                                        onClick={() => {
                                            setIsModalOpen(false);
                                            dispatch(setShowBackToDashboardButton(true));
                                            navigate("/sender-management");
                                        }}
                                    >
                                        <p>{getReviewSenderDetailsTranslation('addSender', isSpanish)}</p>
                                        <ArrowUpRight className='w-5 h-5 stroke-white' />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}